import type { StudioCampaign, StudioChannel, StudioSegment } from "./types";
import { unknownVariables } from "./variables";

export type CheckLevel = "error" | "warning" | "ok";

export type CampaignCheck = {
  level: CheckLevel;
  // translation key under campaigns.studio_checks
  key: string;
  params?: (string | number)[];
  channel?: StudioChannel;
};

const channelTexts = (c: StudioCampaign, channel: StudioChannel): string[] => {
  switch (channel) {
    case "email":
      return [
        c.email.subject,
        c.email.preheader,
        ...c.email.blocks.flatMap((b) =>
          "text" in b ? [b.text] : "label" in b ? [b.label] : "headline" in b ? [b.headline] : [],
        ),
      ];
    case "whatsapp":
      return [c.whatsapp.body, c.whatsapp.header.type === "text" ? c.whatsapp.header.text : ""];
    case "sms":
      return [c.sms.body];
    case "push":
      return [c.push.title, c.push.body];
  }
};

const usesCoupon = (texts: string[]) => texts.some((t) => t.includes("{{coupon_code}}"));

// Quiet hours: 21:00–08:00 local time.
const isQuietHour = (iso: string) => {
  const hour = new Date(iso).getHours();
  return hour >= 21 || hour < 8;
};

export const checkCampaign = (c: StudioCampaign, segment: StudioSegment | undefined): CampaignCheck[] => {
  const out: CampaignCheck[] = [];

  if (!c.name.trim()) out.push({ level: "error", key: "name_missing" });
  if (c.channels.length === 0) out.push({ level: "error", key: "no_channels" });

  for (const channel of c.channels) {
    const texts = channelTexts(c, channel);
    const unknown = [...new Set(texts.flatMap(unknownVariables))];
    if (unknown.length) {
      out.push({ level: "error", key: "unknown_variables", params: [unknown.map((u) => `{{${u}}}`).join(", ")], channel });
    }
    if (c.offer.type === "none" && usesCoupon(texts)) {
      out.push({ level: "error", key: "coupon_without_offer", channel });
    }
    if (segment && segment.reachable[channel] === 0) {
      out.push({ level: "warning", key: "nobody_reachable", channel });
    }
  }

  if (c.channels.includes("email")) {
    if (!c.email.subject.trim()) out.push({ level: "error", key: "email_subject_missing", channel: "email" });
    if (c.email.blocks.length === 0) out.push({ level: "error", key: "email_empty", channel: "email" });
    if (c.offer.type === "coupon" && !c.email.blocks.some((b) => b.type === "coupon") && !usesCoupon(channelTexts(c, "email"))) {
      out.push({ level: "warning", key: "email_no_coupon", channel: "email" });
    }
    out.push({ level: "ok", key: "email_unsubscribe", channel: "email" });
  }

  if (c.channels.includes("whatsapp")) {
    const status = c.whatsapp.approval_status;
    if (status !== "approved") {
      out.push({ level: status === "rejected" ? "error" : "warning", key: `whatsapp_${status}`, channel: "whatsapp" });
    } else {
      out.push({ level: "ok", key: "whatsapp_approved", channel: "whatsapp" });
    }
  }

  if (c.channels.includes("sms")) {
    if (!c.sms.body.trim()) out.push({ level: "error", key: "sms_empty", channel: "sms" });
    out.push(
      c.sms.append_opt_out
        ? { level: "ok", key: "sms_opt_out", channel: "sms" }
        : { level: "error", key: "sms_opt_out_missing", channel: "sms" },
    );
  }

  if (c.channels.includes("push") && !c.push.title.trim()) {
    out.push({ level: "error", key: "push_title_missing", channel: "push" });
  }

  if (c.schedule.type === "later" && isQuietHour(c.schedule.send_at)) {
    out.push({ level: "warning", key: "quiet_hours" });
  }
  if (c.holdout_percent === 0) out.push({ level: "warning", key: "no_holdout" });
  else out.push({ level: "ok", key: "holdout", params: [c.holdout_percent] });

  return out;
};

export const worstLevel = (checks: CampaignCheck[]): CheckLevel =>
  checks.some((c) => c.level === "error") ? "error" : checks.some((c) => c.level === "warning") ? "warning" : "ok";

// ----------------------------
// Reach estimate
// ----------------------------

export type ReachEstimate = {
  // Customers that receive at least one message.
  people: number;
  // Messages sent per channel.
  messages: Partial<Record<StudioChannel, number>>;
  holdout: number;
};

// Assumes channel reachability is independent within a segment — good enough
// for a preview; the real number comes from resolving the audience.
export const estimateReach = (c: StudioCampaign, segment: StudioSegment): ReachEstimate => {
  const holdout = Math.round((segment.size * c.holdout_percent) / 100);
  const scale = segment.size === 0 ? 0 : (segment.size - holdout) / segment.size;
  const share = (ch: StudioChannel) => (segment.size === 0 ? 0 : segment.reachable[ch] / segment.size);
  const messages: Partial<Record<StudioChannel, number>> = {};

  if (c.strategy === "all") {
    for (const ch of c.channels) messages[ch] = Math.round(segment.reachable[ch] * scale);
  } else {
    // Each channel only gets customers not reached by an earlier one.
    let unreached = 1;
    for (const ch of c.fallback_order.filter((x) => c.channels.includes(x))) {
      messages[ch] = Math.round(segment.size * scale * unreached * share(ch));
      unreached *= 1 - share(ch);
    }
  }

  const missed = c.channels.reduce((acc, ch) => acc * (1 - share(ch)), 1);
  return { people: Math.round(segment.size * scale * (1 - missed)), messages, holdout };
};
